import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, User, LogIn, UserPlus, LogOut, UserCircle, Calculator, SlidersHorizontal } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { useSignupModal } from '../../context/SignupModalContext';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';

const HeaderBlock = ({ block }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { openSignupModal } = useSignupModal();
  const { totalItems } = useCart();
  const [searchQuery, setSearchQuery] = useState('');
  const { logo_url, site_name, show_search } = block.content;

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (trimmed) {
      navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    } else {
      navigate('/search');
    }
    setSearchQuery('');
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
    navigate('/');
  };

  return (
    <header className="w-full border-b bg-background shadow-sm">
      {/* Container centré max-w-7xl - DESIGN SYSTEM UNIFIÉ */}
      <div className="max-w-7xl mx-auto flex h-16 items-center justify-between px-4">
        {/* Logo et Nom du Site */}
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
          {logo_url ? (
            <img 
              src={logo_url} 
              alt={site_name || 'Logo'} 
              className="h-10 w-10 object-contain"
            />
          ) : null}
          <span 
            className="text-[1.65rem] leading-none font-extrabold tracking-tight"
            style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, letterSpacing: '-0.03em' }}
          >
            {site_name || 'Games Visor'}
          </span>
        </Link>

        {/* Barre de Recherche (Desktop) - Visible seulement si connecté */}
        {show_search && user && (
          <form 
            onSubmit={handleSearch} 
            className="hidden md:flex items-center gap-2 flex-1 max-w-md mx-8"
          >
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="search"
                placeholder="Rechercher un jeu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="header-search-input"
              />
            </div>
            <button
              type="button"
              onClick={() => navigate('/search')}
              className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-colors text-muted-foreground hover:bg-gray-100"
              title="Recherche avancée"
              data-testid="advanced-search-button"
            >
              <SlidersHorizontal className="h-4 w-4" />
            </button>
          </form>
        )}

        {/* Actions Utilisateur */}
        <div className="flex items-center gap-2">
          {user ? (
            // UTILISATEUR CONNECTÉ
            <>
              {/* Desktop */}
              <div className="hidden sm:flex items-center gap-2">
                {/* Nom utilisateur (optionnel) */}
                <span className="text-sm text-muted-foreground px-2">
                  Bonjour, <span className="font-medium text-foreground">{user.pseudo?.split(' ')[0] || 'Utilisateur'}</span>
                </span>
                
                {/* Simulation */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/dashboard/simulation')}
                  className="items-center gap-2"
                  data-testid="header-simulation-button"
                >
                  <Calculator className="h-4 w-4" />
                  <span>Simulation</span>
                  {totalItems > 0 && (
                    <span className="ml-0.5 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-indigo-600 text-white text-[10px] font-bold leading-none" data-testid="header-simulation-badge">
                      {totalItems}
                    </span>
                  )}
                </Button>

                {/* Mon compte */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/dashboard/profil')}
                  className="items-center gap-2"
                  data-testid="header-account-button"
                >
                  <UserCircle className="h-4 w-4" />
                  <span>Mon compte</span>
                </Button>

                {/* Déconnexion */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  className="items-center gap-2"
                  data-testid="header-logout-button"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Déconnexion</span>
                </Button>
              </div>

              {/* Mobile */}
              <div className="flex sm:hidden items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/dashboard/profil')}
                  data-testid="header-account-button-mobile"
                  title="Mon compte"
                >
                  <UserCircle className="h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleLogout}
                  data-testid="header-logout-button-mobile"
                  title="Déconnexion"
                >
                  <LogOut className="h-5 w-5" />
                </Button>
              </div>
            </>
          ) : (
            // UTILISATEUR NON CONNECTÉ
            <>
              {/* Desktop */}
              <div className="hidden sm:flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/login')}
                  className="items-center gap-2"
                  data-testid="header-login-button"
                >
                  <LogIn className="h-4 w-4" />
                  <span>Connexion</span>
                </Button>

                <Button
                  variant="default"
                  size="sm"
                  onClick={openSignupModal}
                  className="items-center gap-2"
                  data-testid="header-signup-button"
                >
                  <UserPlus className="h-4 w-4" />
                  <span>Créer un compte</span>
                </Button>
              </div>

              {/* Mobile */}
              <div className="flex sm:hidden items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/login')}
                  data-testid="header-login-button-mobile"
                  title="Connexion"
                >
                  <LogIn className="h-5 w-5" />
                </Button>
                <Button
                  variant="default"
                  size="icon"
                  onClick={openSignupModal}
                  data-testid="header-signup-button-mobile"
                  title="Créer un compte"
                >
                  <UserPlus className="h-5 w-5" />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Barre de Recherche Mobile - Visible seulement si connecté */}
      {show_search && user && (
        <div className="md:hidden border-t px-4 py-3">
          <form onSubmit={handleSearch}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="search"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="header-search-input-mobile"
              />
            </div>
          </form>
        </div>
      )}
    </header>
  );
};

export default HeaderBlock;
